import React, { useState, useEffect, useCallback } from 'react';
import apiClient from '../../../apiClient';
import './ActiveCandidatesTable.css';

/**
 * ActiveCandidatesTable Component
 * Searchable, sortable table of active candidates
 */
function ActiveCandidatesTable({ filters }) {
    const [candidates, setCandidates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortField, setSortField] = useState('created_at');
    const [sortDirection, setSortDirection] = useState('desc');
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    // Fetch candidates
    const fetchCandidates = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            params.append('excludeRejected', 'true');
            params.append('excludeHired', 'true');
            if (filters?.recruiter) params.append('recruiter', filters.recruiter);
            if (filters?.department) params.append('department', filters.department);

            const response = await apiClient.get(`/ta/candidates?${params.toString()}`);
            if (response.data) {
                // Filter to only active candidates (not rejected, withdrawn, or hired)
                const activeCandidates = (response.data.data || response.data || []).filter(c =>
                    !['Rejected', 'Withdrawn', 'Hired'].includes(c.candidate_stage)
                );
                setCandidates(activeCandidates);
            }
        } catch (err) {
            console.error('Error fetching candidates:', err);
            setCandidates([]);
        }
        setLoading(false);
    }, [filters]);

    useEffect(() => {
        fetchCandidates();
    }, [fetchCandidates]);

    // Filter by search term
    const filteredCandidates = candidates.filter(c => {
        if (!searchTerm) return true;
        const search = searchTerm.toLowerCase();
        return (
            c.candidate_name?.toLowerCase().includes(search) ||
            c.candidate_email?.toLowerCase().includes(search) ||
            c.candidate_stage?.toLowerCase().includes(search) ||
            c.assigned_recruiter?.toLowerCase().includes(search) ||
            c.standard_job_title?.toLowerCase().includes(search)
        );
    });

    // Sort candidates
    const sortedCandidates = [...filteredCandidates].sort((a, b) => {
        let aVal = a[sortField];
        let bVal = b[sortField];

        // Handle null values
        if (aVal === null || aVal === undefined) aVal = '';
        if (bVal === null || bVal === undefined) bVal = '';

        // Date fields
        if (sortField.includes('date') || sortField === 'created_at' || sortField === 'updated_at') {
            aVal = aVal ? new Date(aVal).getTime() : 0;
            bVal = bVal ? new Date(bVal).getTime() : 0;
        }

        if (typeof aVal === 'string') {
            aVal = aVal.toLowerCase();
            bVal = bVal.toLowerCase();
        }

        if (sortDirection === 'asc') {
            return aVal > bVal ? 1 : -1;
        }
        return aVal < bVal ? 1 : -1;
    });

    // Pagination
    const totalPages = Math.ceil(sortedCandidates.length / itemsPerPage);
    const paginatedCandidates = sortedCandidates.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    // Handle sort click
    const handleSort = (field) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    // Get sort indicator
    const getSortIndicator = (field) => {
        if (sortField !== field) return null;
        return sortDirection === 'asc' ? ' ▲' : ' ▼';
    };

    // Get stage badge class
    const getStageBadgeClass = (stage) => {
        if (['Offer Extended', 'Offer Accepted'].includes(stage)) return 'stage-offer';
        if (['HM 1st Interview', 'HM Interview', 'HM 2nd Interview', 'Final Interview'].includes(stage)) return 'stage-interview';
        if (['Submitted to HM', 'Submittal'].includes(stage)) return 'stage-submitted';
        if (['Phone Screen'].includes(stage)) return 'stage-screen';
        if (['Market Visit'].includes(stage)) return 'stage-visit';
        return 'stage-new';
    };

    // Format date
    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };

    if (loading) {
        return (
            <div className="active-candidates-table table-loading">
                <div className="chart-loading-spinner"></div>
            </div>
        );
    }

    return (
        <div className="active-candidates-table">
            <div className="table-header">
                <div className="table-title-section">
                    <h3 className="chart-title">Active Candidates</h3>
                    <span className="table-count">{filteredCandidates.length} candidates</span>
                </div>
                <div className="table-search">
                    <input
                        type="text"
                        placeholder="Search candidates..."
                        value={searchTerm}
                        onChange={(e) => {
                            setSearchTerm(e.target.value);
                            setCurrentPage(1);
                        }}
                        className="search-input"
                    />
                </div>
            </div>

            <div className="table-wrapper">
                <table className="candidates-table">
                    <thead>
                        <tr>
                            <th onClick={() => handleSort('candidate_name')}>
                                Name{getSortIndicator('candidate_name')}
                            </th>
                            <th onClick={() => handleSort('candidate_stage')}>
                                Stage{getSortIndicator('candidate_stage')}
                            </th>
                            <th onClick={() => handleSort('standard_job_title')}>
                                Job Title{getSortIndicator('standard_job_title')}
                            </th>
                            <th onClick={() => handleSort('assigned_recruiter')}>
                                Recruiter{getSortIndicator('assigned_recruiter')}
                            </th>
                            <th onClick={() => handleSort('candidate_source')}>
                                Source{getSortIndicator('candidate_source')}
                            </th>
                            <th onClick={() => handleSort('phone_screen_date')}>
                                Phone Screen{getSortIndicator('phone_screen_date')}
                            </th>
                            <th onClick={() => handleSort('hm_first_interview')}>
                                HM Interview{getSortIndicator('hm_first_interview')}
                            </th>
                            <th onClick={() => handleSort('created_at')}>
                                Created{getSortIndicator('created_at')}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {paginatedCandidates.length > 0 ? (
                            paginatedCandidates.map((candidate) => (
                                <tr key={candidate.id}>
                                    <td className="candidate-name-cell">
                                        <span className="candidate-name">{candidate.candidate_name || 'N/A'}</span>
                                        {candidate.candidate_email && (
                                            <span className="candidate-email">{candidate.candidate_email}</span>
                                        )}
                                    </td>
                                    <td>
                                        <span className={`stage-badge ${getStageBadgeClass(candidate.candidate_stage)}`}>
                                            {candidate.candidate_stage || 'N/A'}
                                        </span>
                                    </td>
                                    <td>{candidate.standard_job_title || '-'}</td>
                                    <td>{candidate.assigned_recruiter || '-'}</td>
                                    <td>{candidate.candidate_source || '-'}</td>
                                    <td>{formatDate(candidate.phone_screen_date)}</td>
                                    <td>{formatDate(candidate.hm_first_interview)}</td>
                                    <td>{formatDate(candidate.created_at)}</td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="8" className="no-data">
                                    {searchTerm ? 'No candidates match your search' : 'No active candidates'}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="table-pagination">
                    <button
                        className="pagination-btn"
                        onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                        disabled={currentPage === 1}
                    >
                        Previous
                    </button>
                    <span className="pagination-info">
                        Page {currentPage} of {totalPages}
                    </span>
                    <button
                        className="pagination-btn"
                        onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                        disabled={currentPage === totalPages}
                    >
                        Next
                    </button>
                </div>
            )}
        </div>
    );
}

export default ActiveCandidatesTable;
